import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Basis, Count, Scope } from "../api/figures";

/**
 * The basis every sales figure in the app is read on.
 *
 * One setting for the whole session rather than a value per screen: a
 * per-page choice is exactly how the dashboard came to show money net of
 * discounts and returns beside units that were neither, and how the analysis
 * page came to show the opposite pair. The control that changes it sits on
 * the sales grid itself — the numbers it moves — and nowhere else.
 *
 * Defaults are billed and all invoices — the largest figure, the one the sales
 * team recognises, and the top of every breakdown.
 */

const BASIS_KEY = "nexpim.sales.basis";
const SCOPE_KEY = "nexpim.sales.scope";
const COUNT_KEY = "nexpim.sales.count";

interface SalesBasis {
  basis: Basis;
  scope: Scope;
  /** Products (a kit is one) or items (a kit's contents, one each). Products is what the accountant counts. */
  count: Count;
  setBasis: (basis: Basis) => void;
  setScope: (scope: Scope) => void;
  setCount: (count: Count) => void;
}

const SalesBasisContext = createContext<SalesBasis>({
  basis: "billed",
  scope: "all",
  count: "products",
  setBasis: () => {},
  setScope: () => {},
  setCount: () => {},
});

function stored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return allowed.includes(value as T) ? (value as T) : fallback;
  } catch {
    // Private windows and blocked site data both throw on read.
    return fallback;
  }
}

export function SalesBasisProvider({ children }: { children: ReactNode }) {
  const [basis, setBasis] = useState<Basis>(() => stored(BASIS_KEY, ["billed", "net"] as const, "billed"));
  const [scope, setScope] = useState<Scope>(() => stored(SCOPE_KEY, ["all", "issued"] as const, "all"));
  const [count, setCount] = useState<Count>(() => stored(COUNT_KEY, ["products", "items"] as const, "products"));

  useEffect(() => {
    try {
      localStorage.setItem(BASIS_KEY, basis);
      localStorage.setItem(SCOPE_KEY, scope);
      localStorage.setItem(COUNT_KEY, count);
    } catch {
      // A preference that cannot be remembered still works for this session.
    }
  }, [basis, scope, count]);

  const value = useMemo(() => ({ basis, scope, count, setBasis, setScope, setCount }), [basis, scope, count]);
  return <SalesBasisContext.Provider value={value}>{children}</SalesBasisContext.Provider>;
}

export function useSalesBasis(): SalesBasis {
  return useContext(SalesBasisContext);
}
